import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  useTheme,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Search, Clear, DateRange, Close, ArrowForward } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useViewPort } from '@/hooks';
import { CreatePostModal } from '@/components/dashboard/CreatePostModal';
import { FeedList } from '@/components/dashboard/FeedList';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store/types';
import { fetchFeedTypes } from '@/store';
import { FeedViewPage } from './FeedViewPage';


export const DashboardPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const feedId = searchParams.get('feedId');
  useAuth();
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { isMobile } = useViewPort();
  const { feedTypes, isLoading: feedTypesLoading, error, isFeedsLoading } = useSelector(
    (state: RootState) => state.feed
  );

  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('');
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Moment | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Moment | null>(null);
  const [hasAttachment, setHasAttachment] = useState(false);

  // Job-specific filters
  const [location, setLocation] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [jobType, setJobType] = useState('');
  const [minSalary, setMinSalary] = useState('');
  const [maxSalary, setMaxSalary] = useState('');
  const [salaryDenomination, setSalaryDenomination] = useState('lakh');
  const [salaryPeriod, setSalaryPeriod] = useState('yearly');

  // Check if selected type is 'job'
  const isJobTypeSelected = feedTypes?.find(type => type._id === selectedType)?.name?.toLowerCase() === 'job';

  // Quick date filter options
  const quickDateFilters = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'this-week' },
    { label: 'Last Week', value: 'last-week' },
  ];

  const getDateRange = (filter: string) => {
    const today = moment();

    switch (filter) {
      case 'today':
        return { start: today.clone(), end: today.clone() };
      case 'yesterday':
        const yesterday = today.clone().subtract(1, 'day');
        return { start: yesterday, end: yesterday.clone() };
      case 'this-week':
        return { start: today.clone().startOf('week'), end: today.clone() };
      case 'last-week':
        return {
          start: today.clone().subtract(1, 'week').startOf('week'),
          end: today.clone().subtract(1, 'week').endOf('week')
        };
      default:
        return { start: null, end: null };
    }
  };

  // Pre-fetch feed types when dashboard loads
  useEffect(() => {
    dispatch(fetchFeedTypes());
  }, [dispatch]);

  const handleCreatePostClose = () => {
    setCreatePostOpen(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value);
  };

  const handleQuickDateFilter = (filter: string) => {
    const { start, end } = getDateRange(filter);
    setTempStartDate(start);
    setTempEndDate(end);
    setSelectedQuickFilter(filter);
    setStartDate(start);
    setEndDate(end);
    setDateDialogOpen(false);
  };

  const openDateDialog = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setDateDialogOpen(true);
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    setSelectedQuickFilter('');
  };

  const applyDateFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setDateDialogOpen(false);
  };

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedQuickFilter('');
    setTempStartDate(null);
    setTempEndDate(null);
    setDateDialogOpen(false);
  };

  const getDateRangeLabel = () => {
    if (selectedQuickFilter) {
      return quickDateFilters.find(f => f.value === selectedQuickFilter)?.label || '';
    }
    if (startDate && endDate) {
      if (startDate.isSame(endDate, 'day')) {
        return startDate.format('MMM DD, YYYY');
      }
      return `${startDate.format('MMM DD')} → ${endDate.format('MMM DD, YYYY')}`;
    }
    if (startDate) {
      return `From ${startDate.format('MMM DD, YYYY')}`;
    }
    if (endDate) {
      return `Until ${endDate.format('MMM DD, YYYY')}`;
    }
    return '';
  };

  const handleSearchSubmit = () => {
    setAppliedSearchQuery(searchQuery.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setSelectedType('all');
    clearDateFilters();
    setHasAttachment(false);
    // Clear job-specific filters
    setLocation('');
    setExperienceLevel('');
    setJobType('');
    setMinSalary('');
    setMaxSalary('');
    setSalaryDenomination('lakh');
    setSalaryPeriod('yearly');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || appliedSearchQuery.trim() !== '' || selectedType !== 'all' || startDate !== null || endDate !== null || hasAttachment || location !== '' || experienceLevel !== '' || jobType !== '' || minSalary !== '' || maxSalary !== '';

  if (feedId) {
    return <FeedViewPage />;
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Filters & Create Section */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          mb: 2,
          // borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Mobile: Create button on top */}
        <Box
          sx={{
            display: { xs: 'flex',},
            mb: 1.5,
          }}
        >
             {/* Search */}
          <TextField
            placeholder="Search posts, jobs, hire me..."
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            disabled={isFeedsLoading}
            size="small"
            sx={{
              flex: { xs: 1,  },
              width: { xs: '100%',  },
              minWidth: { xs: 'auto',  },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                height: 36,
                pr: 0.5,
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleSearchSubmit}
                      variant="contained"
                      size="small"
                      disabled={searchQuery.trim() === '' || isFeedsLoading}
                      startIcon={searchQuery.trim() !== '' && isFeedsLoading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        height: 28,
                        minWidth: 0,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: '#0a66c2',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#004182',
                          boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                          bgcolor: searchQuery.trim() === '' ? undefined : '#0a66c2',
                          color: searchQuery.trim() === '' ? undefined : 'white',
                          opacity: searchQuery.trim() === '' ? undefined : 0.7,
                        },
                      }}
                    >
                      {searchQuery.trim() !== '' && isFeedsLoading ? 'Searching' : 'Search'}
                    </Button>
                  </InputAdornment>
                ),
              },
            }}
          />
     
        </Box>

        {/* Filters row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
        

          {/* Type Filter */}
          <TextField
            select
            size="small"
            value={selectedType}
            onChange={handleTypeChange}
            sx={{
              flex: { xs: 1, sm: 'none' },
              width: { xs: 'auto', sm: 130 },
              minWidth: { xs: 100, sm: 130 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                height: 36,
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
              },
            }}
            disabled={feedTypesLoading || feedTypes?.length === 0 || isFeedsLoading}
          >
            <MenuItem value="all">All Types</MenuItem>
            {feedTypesLoading ? (
              <MenuItem disabled>Loading...</MenuItem>
            ) : error ? (
              <MenuItem disabled>Error</MenuItem>
            ) : (
              feedTypes?.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name.toLowerCase() === 'jobseeker' ? 'Hire me' : type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                </MenuItem>
              ))
            )}
          </TextField>

          {/* Date Filter */}
          <Button
            variant="outlined"
            onClick={openDateDialog}
            startIcon={<DateRange sx={{ fontSize: 16 }} />}
            size="small"
            disabled={isFeedsLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              height: 36,
              px: 1.5,
              borderRadius: 1,
              borderColor: theme.palette.divider,
              color: 'text.primary',
              flexShrink: 0,
              '&:hover': {
                borderColor: theme.palette.divider,
                bgcolor: 'action.hover',
              },
            }}
          >
            {getDateRangeLabel() || 'Date'}
          </Button>

          {/* Has Attachment Filter */}
          <FormControlLabel
            control={
              <Checkbox
                checked={hasAttachment}
                onChange={(e) => setHasAttachment(e.target.checked)}
                disabled={isFeedsLoading}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'text.primary' }}>
                Has Attachment
              </Typography>
            }
            sx={{
              mr: 1,
              ml: 1,
            }}
          />

          {/* Job-specific Filters - Only show when Job type is selected */}
          {isJobTypeSelected && (
            <Grid container spacing={1} sx={{ width: '100%', mt: 1 }}>
              {/* Location Filter */}
              <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                <TextField
                  label="Location"
                  variant="outlined"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Experience Level Filter */}
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  select
                  label="Experience"
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="entry">Fresher / Entry Level</MenuItem>
                  <MenuItem value="junior">Junior Level</MenuItem>
                  <MenuItem value="mid">Mid-Level</MenuItem>
                  <MenuItem value="senior">Senior Level</MenuItem>
                  <MenuItem value="manager">Managerial Level</MenuItem>
                  <MenuItem value="head">Leadership / Head</MenuItem>
                  <MenuItem value="executive">Director / C-Level</MenuItem>
                </TextField>
              </Grid>

              {/* Job Type Filter */}
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <TextField
                  select
                  label="Job Type"
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  value={jobType}
                  onChange={(e) => setJobType(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="full-time">Full-time</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="freelance">Freelance</MenuItem>
                  <MenuItem value="internship">Internship</MenuItem>
                </TextField>
              </Grid>

              {/* Salary Min */}
              <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                <TextField
                  placeholder="Min Salary"
                  variant="outlined"
                  type="number"
                  value={minSalary}
                  onChange={(e) => setMinSalary(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Salary Max */}
              <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                <TextField
                  placeholder="Max Salary"
                  variant="outlined"
                  type="number"
                  value={maxSalary}
                  onChange={(e) => setMaxSalary(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Salary Denomination */}
              <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  value={salaryDenomination}
                  onChange={(e) => setSalaryDenomination(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                >
                  <MenuItem value="thousand">Thousand</MenuItem>
                  <MenuItem value="lakh">Lakh</MenuItem>
                  <MenuItem value="crore">Crore</MenuItem>
                </TextField>
              </Grid>

              {/* Salary Period */}
              <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  disabled={isFeedsLoading}
                  value={salaryPeriod}
                  onChange={(e) => setSalaryPeriod(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      height: 36,
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                    },
                  }}
                >
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}

    {isMobile && <Button
            variant="contained"
            onClick={() => setCreatePostOpen(true)}
            fullWidth
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 40,
              borderRadius: 1,
              bgcolor: '#0a66c2',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#004182',
                boxShadow: 'none',
              },
            }}
          >
            + Create Talky
          </Button>}
          {/* Clear Filters */}
          {hasActiveFilters && (
            <Tooltip title="Clear filters">
              <IconButton
                onClick={handleClearFilters}
                disabled={isFeedsLoading}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  color: 'text.secondary',
                  flexShrink: 0,
                  '&:hover': {
                    color: 'error.main',
                    borderColor: 'error.main',
                  },
                }}
              >
                <Clear sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Spacer - desktop only */}
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

          {/* Create Button - desktop only */}
          <Button
            variant="contained"
            onClick={() => setCreatePostOpen(true)}
            size="small"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 36,
              px: 2,
              borderRadius: 1,
              bgcolor: '#0a66c2',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#004182',
                boxShadow: 'none',
              },
            }}
          >
            + Create Talky
          </Button>
        </Box>
      </Paper>

     

      {/* Posts Feed */}
      <FeedList
        refreshTrigger={refreshTrigger}
        selectedType={selectedType}
        searchQuery={appliedSearchQuery}
        startDate={startDate}
        endDate={endDate}
        hasAttachment={hasAttachment}
        location={isJobTypeSelected ? location : undefined}
        experienceLevel={isJobTypeSelected ? experienceLevel : undefined}
        jobType={isJobTypeSelected ? jobType : undefined}
        minSalary={isJobTypeSelected ? minSalary : undefined}
        maxSalary={isJobTypeSelected ? maxSalary : undefined}
        salaryDenomination={isJobTypeSelected && (minSalary || maxSalary) ? salaryDenomination : undefined}
        salaryPeriod={isJobTypeSelected && (minSalary || maxSalary) ? salaryPeriod : undefined}
      />

      {/* Create Post Modal */}
      <CreatePostModal
        open={createPostOpen}
        onClose={handleCreatePostClose}
      />

      {/* Date Range Dialog */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Dialog
          open={dateDialogOpen}
          onClose={closeDateDialog}
          maxWidth="sm"
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                margin: 2,
                maxHeight: '80vh',
              }
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                {tempStartDate && tempEndDate ? (
                  <Typography variant="h6">
                    {tempStartDate.format('MMM DD, YYYY')}
                    <ArrowForward sx={{ mx: 1, fontSize: 16 }} />
                    {tempEndDate.format('MMM DD, YYYY')}
                  </Typography>
                ) : (
                  <Typography variant="h6">Select Date Range</Typography>
                )}
              </Box>
              <IconButton onClick={closeDateDialog} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
            {/* Quick Filters Section */}
            <Box sx={{
              width: '100%',
              p: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
            }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Quick Filters
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                {quickDateFilters.map((filter) => (
                  <Button
                    key={filter.value}
                    onClick={() => handleQuickDateFilter(filter.value)}
                    variant={selectedQuickFilter === filter.value ? 'contained' : 'text'}
                    size="small"
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: selectedQuickFilter === filter.value ? 'white' : 'text.primary',
                      bgcolor: selectedQuickFilter === filter.value ? 'primary.main' : 'transparent',
                      '&:hover': {
                        bgcolor: selectedQuickFilter === filter.value ? 'primary.dark' : 'action.hover',
                      },
                    }}
                  >
                    {filter.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={clearDateFilters}
              color="error"
              size="small"
            >
              Clear All
            </Button>
            <Button
              onClick={closeDateDialog}
              size="small"
            >
              Cancel
            </Button>
            <Button
              onClick={applyDateFilter}
              variant="contained"
              disabled={!tempStartDate && !tempEndDate && !selectedQuickFilter}
              size="small"
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};